import { ArchiveFileType } from "@/lib/supabase/supabaseTypes";
import {
	FileTypeWithDocumentType,
	getFileTypeWithDocumentType,
} from "@/lib/utils/file-utils";

/**
 * German labels for the document_kind the view computes (archive_document_kind in SQL). The
 * key vocabulary is the single source of truth on both sides - the labels live here so the UI
 * stays German while the database stays language-neutral.
 */
export const ARCHIVE_KIND_LABELS: Record<string, string> = {
	rechnung: "Rechnung",
	rechnungsdoppel: "Rechnungsdoppel",
	sammelrechnung: "Sammelrechnung",
	akonto: "Akontorechnung",
	gutschrift: "Gutschrift",
	mahnung: "Mahnung",
	quittung: "Quittung",
	einzahlungsschein: "Einzahlungsschein",
	lieferschein: "Lieferschein",
	kostenvoranschlag: "Kostenvoranschlag",
	auftragsblatt: "Auftragsblatt",
	etikette: "Etikette",
	patientenpass: "Patientenpass",
	email: "E-Mail",
	xml: "XML",
	bild: "Bild",
	scan3d: "3D-Scan",
	video: "Video",
	text: "Text",
	dokument: "Dokument",
	sonstiges: "Sonstiges",
};

export const getArchiveKindLabel = (kind: string | null): string =>
	(kind && ARCHIVE_KIND_LABELS[kind]) || "Datei";

/**
 * The label a single file row shows in the "Art" column: the invoice number for a file that is
 * an invoice's PDF or attached to one (QR slip, reminder), cancelled invoices marked.
 */
export const getArchiveFileLabel = (file: ArchiveFileType): string => {
	const kind = getArchiveKindLabel(file.document_kind);
	const label = file.invoice_doc_number
		? `${kind} · ${file.invoice_doc_number}`
		: kind;
	return file.invoice_canceled_at ? `${label} (storniert)` : label;
};

/** The icon variant - keeps the specific LS/KV/GS icons via the existing meta_data helper. */
export const getArchiveIconType = (
	file: ArchiveFileType
): FileTypeWithDocumentType =>
	getFileTypeWithDocumentType(
		file.meta_data as unknown as Record<string, any> | null,
		file.file_name
	);

export const formatArchiveDate = (date: string | null): string => {
	if (!date) return "-";
	return new Date(date).toLocaleDateString("de-DE", {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
	});
};

/** Header subtitle like "2016–2026" or a single year, empty when the span is unknown. */
export const formatArchiveDateRange = (
	minDate: string | null,
	maxDate: string | null
): string => {
	if (!minDate || !maxDate) return "";
	const from = new Date(minDate).getFullYear();
	const to = new Date(maxDate).getFullYear();
	return from === to ? String(from) : `${from}–${to}`;
};
